// ─────────────────────────────────────────────────────────────────────────────
// What a delivery run costs: distance, time, and roughly how much battery.
//
// Distance is real (haversine over the actual stops). Time and battery are a
// MODEL — a flat drain rate, a fixed descend/climb per stop and the winch hover
// worked out from rope length and winch rate — good enough to warn an operator
// before launch, not good enough to plan against. The constants are the tuning
// knobs; a per-airframe consumption profile replaces them when the number has
// to be trusted rather than glanced at.
// ─────────────────────────────────────────────────────────────────────────────

import { roundTripMeters } from './geo';

// ponytail: flat drain rate, no airframe/payload/wind term. Swap for a measured
// per-airframe profile if these numbers ever gate a decision automatically.
export const BATTERY_PERCENT_PER_MINUTE = 1.8;
export const STOP_TRANSITION_SEC = 35;  // descend from cruise to the hover, climb back out
export const BATTERY_RESERVE_PERCENT = 20;  // never plan to land on empty
export const DEFAULT_NAV_SPEED_MPS = 5;

// Mirrors backend/utils/deliveryMission.js — keep the two in step. The aircraft
// hovers while the winch runs, once to lower and once to retract, and at the
// stock 0.5 m/s that hover is most of a stop.
const WINCH_TAIL_SECONDS = 5;
const HOOK_RELEASE_SECONDS = 2;
const DEFAULT_WINCH = { ropeLength: 15, winchRate: 0.5, settleHold: 2, postDropHold: 2 };

/**
 * Seconds one stop spends hovering on the winch: settle, lower, release,
 * retract. Missing or unusable fields fall back to the system defaults, so the
 * figure is never smaller than a real stop just because a field was left blank.
 */
export function winchHoverSeconds(winch = {}) {
  const num = (key, min) => {
    const raw = winch ? winch[key] : undefined;
    const n = Number(raw);
    return raw !== '' && raw != null && Number.isFinite(n) && n >= min ? n : DEFAULT_WINCH[key];
  };
  const leg = Math.ceil(num('ropeLength', 0.1) / num('winchRate', 0.01)) + WINCH_TAIL_SECONDS;
  const settle = Math.ceil(num('settleHold', 0));
  return settle + (leg + settle) + HOOK_RELEASE_SECONDS + (leg + Math.ceil(num('postDropHold', 0)));
}

/**
 * Round trip from `start`, through `stops` in order, back to `start`.
 * Returns null when there is no usable start point or no stops — the caller
 * shows "unknown" rather than a fabricated figure.
 *
 * `winch` is { ropeLength, winchRate, settleHold, postDropHold }; see
 * winchHoverSeconds.
 */
export function estimateDelivery({ start, stops, navSpeed, payloadKg = 0, winch }) {
  const meters = roundTripMeters(start, stops);
  if (meters <= 0) return null;

  const speed = Number(navSpeed) > 0 ? Number(navSpeed) : DEFAULT_NAV_SPEED_MPS;
  const stopCount = (stops || []).length;
  const seconds = meters / speed + stopCount * (STOP_TRANSITION_SEC + winchHoverSeconds(winch));
  const minutes = seconds / 60;

  return {
    meters,
    km: meters / 1000,
    minutes,
    payloadKg,
    batteryPercent: Math.min(100, Math.round(minutes * BATTERY_PERCENT_PER_MINUTE)),
  };
}

/**
 * Is there enough charge for this run plus the reserve?
 *
 * `null` when the drone is not reporting a battery level — unknown is not the
 * same as fine, and the caller says so rather than showing a green tick.
 */
export function batteryVerdict(estimate, batteryRemaining) {
  const have = Number(batteryRemaining);
  if (!estimate || !Number.isFinite(have)) return null;

  const need = estimate.batteryPercent + BATTERY_RESERVE_PERCENT;
  return {
    have: Math.round(have),
    need,
    ok: have >= need,
    tight: have >= estimate.batteryPercent && have < need,
  };
}
